import React, {useState} from "react";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {Box, List, ListItem} from "@material-ui/core";
import {Button, Grid, IconButton, Typography} from "@deere/fuel-react";
import {fetchSuppliersList} from "../shared/services/supplierSearchService";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from "@material-ui/icons/Add";
import TextField from "@material-ui/core/TextField";
import {useAuthConfig} from "../shared/helper";
import {Loading} from "../shared/Loading";
import DeleteIcon from "@material-ui/icons/Delete";
import Stack from "@mui/material/Stack";


/**
 * GroupCard component for displaying and managing supplier groups
 * @param {Object} props - Component props
 * @param {string} props.parentName - Name of the parent group
 * @param {Array<Object>} props.suppliers - Array of supplier objects
 * @param {Function} props.updateGroups - Function to update the supplier groups
 * @param {Array<string>} props.fyOptions - Array of fiscal year options
 * @returns {JSX.Element} Rendered GroupCard component
 */
export const GroupCard = ({parentName, suppliers, updateGroups, fyOptions}) => {
    let authAxios = useAuthConfig();
    const fiscalYear = suppliers && suppliers.length > 0 ? suppliers[0].fiscalYear : null;
    const [isEditing, setIsEditing] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [supplierOptions, setSupplierOptions] = useState([]);
    const [supplierSearch, setSupplierSearch] = useState(null); // Local state for Autocomplete
    const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
    const [supplierSearchError, setSupplierSearchError] = useState("");

    const [suppliersToAdd, setSuppliersToAdd] = useState([]);
    const [suppliersToRemove, setSuppliersToRemove] = useState([]);
    const [newParentName, setNewParentName] = useState(parentName);
    const [expanded, setExpanded] = useState(false);

    const getSupplierNumberLabel = (suppliers, len) => {
        if (!suppliers || suppliers.length === 0) {
            return "";
        }
        let result = "";
        const end = Math.min(suppliers.length, len);
        for (let index = 0; index < end; index++) {
            if (index < end - 1) {
                result += `${suppliers[index].supplierNumber}, `;
            } else {
                result += suppliers[index].supplierNumber;
                if (end !== suppliers.length) {
                    result += `... (${suppliers.length})`;
                }
            }
        }
        return result;
    }

    async function handleSupplierSearchInputChange(event, inputValue) {
        if (inputValue && inputValue.length >= 3) {
            try {
                const response = await fetchSuppliersList(authAxios, inputValue);
                setSupplierOptions(response);
            } catch (error) {
                console.error('Error fetching suppliers:', error);
                setSupplierOptions([]);
            }
        } else {
            setSupplierOptions([]);
        }
    }

    const resetState = () => {
        setIsEditing(false);
        setNewParentName(parentName);
        setSuppliersToAdd([]);
        setSuppliersToRemove([]);
        setSupplierSearch(null);
        setSupplierSearchError("");
    }

    async function addSuppliersToGroup() {
        if (!supplierSearch) {
            setSupplierSearchError("Supplier selection is required");
            return;
        }
        if (suppliers.some((supplier) => supplier.supplierNumber === supplierSearch.supplierNumber) ||
            suppliersToAdd?.some((supplier) => supplier.supplierNumber === supplierSearch.supplierNumber)) {
            setSupplierSearchError("Supplier is already in the group");
            return;
        }
        setSupplierSearchLoading(true);
        const isValidSupplier = await authAxios.post("/supplierParent", {
            supplierNum: supplierSearch.supplierNumber,
            fiscalYear: fiscalYear,
        }).then((response) => {
            if (!!response.data?.parentName) {
                setSupplierSearchError("Supplier is already in a group");
                return false;
            }
            return true;
        }).catch(() => {
            console.error("Error checking if supplier exists in group");
            return false;
        }).finally(
            () => setSupplierSearchLoading(false)
        )
        if (!isValidSupplier) {
            return;
        }
        setSupplierSearchError("");
        setSuppliersToAdd([...suppliersToAdd, {
            fiscalYear: fiscalYear,
            parentName: "",
            supplierName: supplierSearch.supplierName,
            supplierNumber: supplierSearch.supplierNumber
        }]);
        setSupplierSearch(null);
    }

    function onDelete() {
        setSaveLoading(true);
        authAxios.delete(`/parentGroup/${parentName}/${fiscalYear}`).then((response) => {
            console.log('Group deleted successfully:', response.data);
            updateGroups({[parentName]: suppliers}, undefined);
            resetState();
        }).catch((error) => {
            console.error('Error deleting group:', error);
            setSupplierSearchError("Failed to delete group. Please try again.");
        }).finally(() => setSaveLoading(false));
    }

    function onSubmit() {
        const newSuppliers = [...suppliers.filter(supplier =>
            !suppliersToRemove.some(suppliersToRemoveValue => supplier.supplierNumber === suppliersToRemoveValue.supplierNumber)
        ), ...suppliersToAdd]
        if (newSuppliers.length < 2) {
            setSupplierSearchError("At least two suppliers must be present in the group.");
            return;
        }
        const payload = {
            oldParentName: parentName,
            newParentName: newParentName,
            suppliers: newSuppliers,
            fiscalYear: fiscalYear
        };
        setSaveLoading(true);
        authAxios.post('/parentGroup/update', payload).then((response) => {
            // API should return a singleton map
            console.log('Group updated successfully:', response.data);
            updateGroups({[parentName]: suppliers}, response.data);
            resetState();
        }).catch((error) => {
            console.error('Error updating group:', error);
            setSupplierSearchError("Failed to update group. Please try again.");
        }).finally(() => setSaveLoading(false));
    }

    const SupplierListItem = ({supplier}) => (
        <>
            <Grid item xs={isEditing ? 11 : 12} sm={isEditing ? 5 : 6}>
                <Typography variant="subtitle1">
                    {supplier.supplierName} (#{supplier.supplierNumber})
                </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">
                    Fiscal Year: {supplier.fiscalYear}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">
                    Parent: {supplier.parentName}
                </Typography>
            </Grid>
        </>
    )

    return <>
        <Accordion expanded={expanded} onChange={(event, isExpanded) => {
            setExpanded(isExpanded);
            if (!isExpanded) {
                resetState();
            }
        }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        {isEditing ? (
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item>
                                    <TextField
                                        variant={"outlined"}
                                        value={newParentName}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={e => setNewParentName(e.target.value)}/>
                                </Grid>
                                {saveLoading ? <Loading/> :
                                <Grid item>
                                    <IconButton onClick={(event) => {
                                        event.stopPropagation();
                                        onDelete();
                                    }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>}
                            </Grid>
                            ) : parentName}
                    </Grid>
                    <Grid item>
                        <Typography variant="body2" color="textSecondary">
                            {getSupplierNumberLabel(suppliers, 3)}
                        </Typography>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                <List>
                    {suppliers && suppliers.length > 0 ?
                        suppliers.filter(supplier =>
                            !suppliersToRemove.some(suppliersToRemoveValue => supplier.supplierNumber === suppliersToRemoveValue.supplierNumber)
                        ).map((supplier, idx) => (
                            <ListItem key={idx} alignItems="flex-start" divider>
                                <Box width="100%">
                                    <Grid container spacing={1} alignItems="center">
                                        {isEditing &&
                                            <Grid item xs={1} sm={1}>
                                                <IconButton onClick={() => {
                                                    setSuppliersToRemove([...suppliersToRemove, supplier]);
                                                }}>
                                                    <RemoveIcon/>
                                                </IconButton>
                                            </Grid>}
                                        <SupplierListItem supplier={supplier}/>
                                    </Grid>
                                </Box>
                            </ListItem>
                        )) : (
                            <Typography variant="body2" color="textSecondary">No suppliers found.</Typography>
                        )}
                    {isEditing && suppliersToRemove.length > 0 &&
                        suppliersToRemove.map((supplier, idx) => (
                            <ListItem key={idx} alignItems="flex-start" divider>
                                <Box width="100%" sx={{backgroundColor: '#ffdddd'}}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={1} sm={1}>
                                            <IconButton onClick={() => {
                                                setSuppliersToRemove(suppliersToRemove.filter(s => s.supplierNumber !== supplier.supplierNumber));
                                            }}>
                                                <AddIcon/>
                                            </IconButton>
                                        </Grid>
                                        <SupplierListItem supplier={supplier}/>
                                    </Grid>
                                </Box>
                            </ListItem>
                        ))
                    }
                    {isEditing && suppliersToAdd.length > 0 &&
                        suppliersToAdd.map((supplier, idx) => (
                            <ListItem key={idx} alignItems="flex-start" divider>
                                <Box width="100%" sx={{backgroundColor: '#ddffdd'}}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={1} sm={1}>
                                            <IconButton onClick={() => {
                                                setSuppliersToAdd(suppliersToAdd.filter(s => s.supplierNumber !== supplier.supplierNumber));
                                            }}>
                                                <RemoveIcon/>
                                            </IconButton>
                                        </Grid>
                                        <SupplierListItem supplier={supplier}/>
                                    </Grid>
                                </Box>
                            </ListItem>
                        ))
                    }
                    <ListItem key={'supplier-add'} alignItems="flex-start">
                        {isEditing &&
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={10}>
                                    <Autocomplete
                                        value={supplierSearch}
                                        onChange={(e, value) => {
                                            setSupplierSearch(value);
                                            setSupplierSearchError("");
                                        }}
                                        onInputChange={handleSupplierSearchInputChange}
                                        options={supplierOptions}
                                        getOptionLabel={option => option ? `${option.supplierName} - ${parseInt(option.supplierNumber)}` : ''}
                                        getOptionSelected={(option, value) => option.supplierNumber === value.supplierNumber}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={!!supplierSearchError}
                                                fullWidth
                                                helperText={supplierSearchError}
                                                label="Search Supplier"
                                                placeholder="Type 3+ characters to search..."
                                                required
                                                variant="outlined"
                                            />
                                        )}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    {supplierSearchLoading ? <Loading/> :
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={addSuppliersToGroup}
                                            disabled={!!supplierSearchError}
                                        >
                                            Add
                                        </Button>}
                                </Grid>
                            </Grid>}
                        {isEditing ? (
                            (
                                <Stack direction={'row'} spacing={1}>
                                    <Button onClick={resetState} disabled={saveLoading}>Cancel</Button>
                                    <Button onClick={onSubmit} variant={'primary'} disabled={saveLoading}>Save</Button>
                                </Stack>
                            )
                        ) : (
                            fiscalYear >= Math.max(...fyOptions)-1 && <Button onClick={() => setIsEditing(true)}>Edit</Button>
                            )
                        }
                    </ListItem>
                </List>
            </AccordionDetails>
        </Accordion>
    </>;
}
